/**
 * IATA airport code → IANA timezone name lookup.
 *
 * Used as a fallback when a Transport record was saved before depTz/arrTz
 * fields were introduced, so we can still calculate accurate durations and
 * countdowns for flights between known airports.
 *
 * Covers ~200 airports handling the vast majority of international traffic.
 */
export const AIRPORT_TZ: Record<string, string> = {
  // ── Europe ───────────────────────────────────────────────────────────────
  LHR: 'Europe/London', LGW: 'Europe/London', STN: 'Europe/London',
  LTN: 'Europe/London', LCY: 'Europe/London', MAN: 'Europe/London',
  EDI: 'Europe/London', BHX: 'Europe/London', GLA: 'Europe/London',
  CDG: 'Europe/Paris',  ORY: 'Europe/Paris',
  AMS: 'Europe/Amsterdam',
  FRA: 'Europe/Berlin', MUC: 'Europe/Berlin', BER: 'Europe/Berlin',
  DUS: 'Europe/Berlin', HAM: 'Europe/Berlin', STR: 'Europe/Berlin',
  CGN: 'Europe/Berlin',
  MAD: 'Europe/Madrid', BCN: 'Europe/Madrid', AGP: 'Europe/Madrid',
  PMI: 'Europe/Madrid', VLC: 'Europe/Madrid', SVQ: 'Europe/Madrid',
  FCO: 'Europe/Rome',   MXP: 'Europe/Rome',   VCE: 'Europe/Rome',
  NAP: 'Europe/Rome',   BLQ: 'Europe/Rome',   PSA: 'Europe/Rome',
  ZRH: 'Europe/Zurich', GVA: 'Europe/Zurich', BSL: 'Europe/Zurich',
  VIE: 'Europe/Vienna', SZG: 'Europe/Vienna', INN: 'Europe/Vienna',
  BRU: 'Europe/Brussels', CRL: 'Europe/Brussels',
  CPH: 'Europe/Copenhagen',
  ARN: 'Europe/Stockholm', GOT: 'Europe/Stockholm',
  OSL: 'Europe/Oslo',   TRF: 'Europe/Oslo',
  HEL: 'Europe/Helsinki',
  DUB: 'Europe/Dublin', ORK: 'Europe/Dublin',
  LIS: 'Europe/Lisbon', OPO: 'Europe/Lisbon', FAO: 'Europe/Lisbon',
  ATH: 'Europe/Athens', SKG: 'Europe/Athens',
  IST: 'Europe/Istanbul', SAW: 'Europe/Istanbul',
  WAW: 'Europe/Warsaw', KRK: 'Europe/Warsaw',
  PRG: 'Europe/Prague',
  BUD: 'Europe/Budapest',
  OTP: 'Europe/Bucharest',
  SOF: 'Europe/Sofia',
  ZAG: 'Europe/Zagreb',
  BEG: 'Europe/Belgrade',
  LJU: 'Europe/Ljubljana',
  RIX: 'Europe/Riga',
  TLL: 'Europe/Tallinn',
  VNO: 'Europe/Vilnius',
  SVO: 'Europe/Moscow',  DME: 'Europe/Moscow', SHE: 'Europe/Moscow',

  // ── Americas ─────────────────────────────────────────────────────────────
  JFK: 'America/New_York',  LGA: 'America/New_York',  EWR: 'America/New_York',
  BOS: 'America/New_York',  PHL: 'America/New_York',  BWI: 'America/New_York',
  IAD: 'America/New_York',  DCA: 'America/New_York',  RDU: 'America/New_York',
  CLT: 'America/New_York',  PIT: 'America/New_York',  BUF: 'America/New_York',
  ATL: 'America/New_York',  MIA: 'America/New_York',  FLL: 'America/New_York',
  MCO: 'America/New_York',  TPA: 'America/New_York',  JAX: 'America/New_York',
  DTW: 'America/New_York',  CVG: 'America/New_York',
  ORD: 'America/Chicago',   MDW: 'America/Chicago',   DFW: 'America/Chicago',
  IAH: 'America/Chicago',   HOU: 'America/Chicago',   MCI: 'America/Chicago',
  MSP: 'America/Chicago',   STL: 'America/Chicago',   MSY: 'America/Chicago',
  MKE: 'America/Chicago',   OMA: 'America/Chicago',   MEM: 'America/Chicago',
  LAX: 'America/Los_Angeles', SFO: 'America/Los_Angeles', SJC: 'America/Los_Angeles',
  SEA: 'America/Los_Angeles', LAS: 'America/Los_Angeles', SAN: 'America/Los_Angeles',
  PDX: 'America/Los_Angeles', SNA: 'America/Los_Angeles', BUR: 'America/Los_Angeles',
  SMF: 'America/Los_Angeles', OAK: 'America/Los_Angeles',
  DEN: 'America/Denver',    SLC: 'America/Denver',    ABQ: 'America/Denver',
  PHX: 'America/Phoenix',   TUS: 'America/Phoenix',
  HNL: 'Pacific/Honolulu',  OGG: 'Pacific/Honolulu',
  ANC: 'America/Anchorage',
  YYZ: 'America/Toronto',   YOW: 'America/Toronto',   YUL: 'America/Toronto',
  YVR: 'America/Vancouver', YYC: 'America/Edmonton',  YEG: 'America/Edmonton',
  YWG: 'America/Winnipeg',  YHZ: 'America/Halifax',
  MEX: 'America/Mexico_City', CUN: 'America/Cancun',  GDL: 'America/Mexico_City',
  MTY: 'America/Monterrey',
  GRU: 'America/Sao_Paulo', CGH: 'America/Sao_Paulo', VCP: 'America/Sao_Paulo',
  GIG: 'America/Sao_Paulo', SSA: 'America/Bahia',     REC: 'America/Recife',
  FOR: 'America/Fortaleza',
  EZE: 'America/Argentina/Buenos_Aires', AEP: 'America/Argentina/Buenos_Aires',
  SCL: 'America/Santiago',
  BOG: 'America/Bogota',
  LIM: 'America/Lima',
  UIO: 'America/Guayaquil',
  CCS: 'America/Caracas',
  PTY: 'America/Panama',
  SJO: 'America/Costa_Rica',
  GUA: 'America/Guatemala',

  // ── Middle East ──────────────────────────────────────────────────────────
  DXB: 'Asia/Dubai',    DWC: 'Asia/Dubai',
  AUH: 'Asia/Dubai',
  DOH: 'Asia/Qatar',
  KWI: 'Asia/Kuwait',
  RUH: 'Asia/Riyadh',   JED: 'Asia/Riyadh',   MED: 'Asia/Riyadh',
  BAH: 'Asia/Bahrain',
  MCT: 'Asia/Muscat',
  TLV: 'Asia/Jerusalem',
  AMM: 'Asia/Amman',
  BEY: 'Asia/Beirut',
  BGW: 'Asia/Baghdad',  BSR: 'Asia/Baghdad',
  THR: 'Asia/Tehran',   IKA: 'Asia/Tehran',

  // ── Africa ───────────────────────────────────────────────────────────────
  CAI: 'Africa/Cairo',
  NBO: 'Africa/Nairobi',  MBA: 'Africa/Nairobi',
  JNB: 'Africa/Johannesburg', CPT: 'Africa/Johannesburg', DUR: 'Africa/Johannesburg',
  CMN: 'Africa/Casablanca', RAK: 'Africa/Casablanca',
  TUN: 'Africa/Tunis',
  ALG: 'Africa/Algiers',
  ADD: 'Africa/Addis_Ababa',
  ACC: 'Africa/Accra',
  LOS: 'Africa/Lagos',   ABV: 'Africa/Lagos',
  DAR: 'Africa/Dar_es_Salaam',
  EBB: 'Africa/Kampala',
  HRE: 'Africa/Harare',
  LUN: 'Africa/Lusaka',
  MRU: 'Indian/Mauritius',
  SEZ: 'Indian/Mahe',

  // ── South & Central Asia ─────────────────────────────────────────────────
  BOM: 'Asia/Kolkata',  DEL: 'Asia/Kolkata',  MAA: 'Asia/Kolkata',
  BLR: 'Asia/Kolkata',  HYD: 'Asia/Kolkata',  CCU: 'Asia/Kolkata',
  AMD: 'Asia/Kolkata',  COK: 'Asia/Kolkata',  GOI: 'Asia/Kolkata',
  CMB: 'Asia/Colombo',
  DAC: 'Asia/Dhaka',
  KTM: 'Asia/Kathmandu',
  KHI: 'Asia/Karachi',  LHE: 'Asia/Karachi',  ISB: 'Asia/Karachi',
  KBL: 'Asia/Kabul',
  TAS: 'Asia/Tashkent',
  ALA: 'Asia/Almaty',

  // ── East & Southeast Asia ────────────────────────────────────────────────
  PEK: 'Asia/Shanghai',  PKX: 'Asia/Shanghai',
  PVG: 'Asia/Shanghai',  SHA: 'Asia/Shanghai',
  CAN: 'Asia/Shanghai',  SZX: 'Asia/Shanghai',
  CTU: 'Asia/Shanghai',  CKG: 'Asia/Shanghai',
  KMG: 'Asia/Shanghai',  XIY: 'Asia/Shanghai',
  WUH: 'Asia/Shanghai',  TSN: 'Asia/Shanghai',
  HKG: 'Asia/Hong_Kong',
  TPE: 'Asia/Taipei',    RMQ: 'Asia/Taipei',
  ICN: 'Asia/Seoul',     GMP: 'Asia/Seoul',
  NRT: 'Asia/Tokyo',     HND: 'Asia/Tokyo',
  KIX: 'Asia/Tokyo',     NGO: 'Asia/Tokyo',   CTS: 'Asia/Tokyo',
  SIN: 'Asia/Singapore',
  KUL: 'Asia/Kuala_Lumpur', PEN: 'Asia/Kuala_Lumpur', BKI: 'Asia/Kuching',
  BKK: 'Asia/Bangkok',   HKT: 'Asia/Bangkok', CNX: 'Asia/Bangkok',
  SGN: 'Asia/Ho_Chi_Minh', DAD: 'Asia/Ho_Chi_Minh',
  HAN: 'Asia/Bangkok',
  CGK: 'Asia/Jakarta',   SUB: 'Asia/Jakarta',
  DPS: 'Asia/Makassar',
  MNL: 'Asia/Manila',    CEB: 'Asia/Manila',
  RGN: 'Asia/Rangoon',
  PNH: 'Asia/Phnom_Penh',
  VTE: 'Asia/Vientiane',

  // ── Pacific & Oceania ────────────────────────────────────────────────────
  SYD: 'Australia/Sydney',  MEL: 'Australia/Melbourne',
  BNE: 'Australia/Brisbane', CNS: 'Australia/Brisbane',
  PER: 'Australia/Perth',
  ADL: 'Australia/Adelaide',
  HBA: 'Australia/Hobart',
  DRW: 'Australia/Darwin',
  CBR: 'Australia/Sydney',
  AKL: 'Pacific/Auckland',  CHC: 'Pacific/Auckland',  WLG: 'Pacific/Auckland',
  NAN: 'Pacific/Fiji',
  PPT: 'Pacific/Tahiti',
  GUM: 'Pacific/Guam',
  NOU: 'Pacific/Noumea',
};

/**
 * City/place name → IANA timezone, keyed on lowercase city name.
 * Used as a last-resort fallback when a transport record has no fromCode/toCode
 * and the from/to string doesn't contain a parenthesised IATA code.
 */
export const CITY_TZ: Record<string, string> = {
  // Europe
  'london': 'Europe/London',
  'paris': 'Europe/Paris',
  'amsterdam': 'Europe/Amsterdam',
  'berlin': 'Europe/Berlin',
  'frankfurt': 'Europe/Berlin',
  'munich': 'Europe/Berlin',
  'hamburg': 'Europe/Berlin',
  'madrid': 'Europe/Madrid',
  'barcelona': 'Europe/Madrid',
  'rome': 'Europe/Rome',
  'milan': 'Europe/Rome',
  'venice': 'Europe/Rome',
  'naples': 'Europe/Rome',
  'zurich': 'Europe/Zurich',
  'geneva': 'Europe/Zurich',
  'vienna': 'Europe/Vienna',
  'brussels': 'Europe/Brussels',
  'bruges': 'Europe/Brussels',
  'brugge': 'Europe/Brussels',
  'bruxelles-midi': 'Europe/Brussels',
  'copenhagen': 'Europe/Copenhagen',
  'stockholm': 'Europe/Stockholm',
  'oslo': 'Europe/Oslo',
  'helsinki': 'Europe/Helsinki',
  'lisbon': 'Europe/Lisbon',
  'porto': 'Europe/Lisbon',
  'athens': 'Europe/Athens',
  'warsaw': 'Europe/Warsaw',
  'prague': 'Europe/Prague',
  'budapest': 'Europe/Budapest',
  'bucharest': 'Europe/Bucharest',
  'kiev': 'Europe/Kiev',
  'kyiv': 'Europe/Kiev',
  'moscow': 'Europe/Moscow',
  'st. petersburg': 'Europe/Moscow',
  'istanbul': 'Europe/Istanbul',
  'ankara': 'Europe/Istanbul',
  'dublin': 'Europe/Dublin',
  // Middle East
  'dubai': 'Asia/Dubai',
  'abu dhabi': 'Asia/Dubai',
  'doha': 'Asia/Qatar',
  'riyadh': 'Asia/Riyadh',
  'jeddah': 'Asia/Riyadh',
  'kuwait city': 'Asia/Kuwait',
  'muscat': 'Asia/Muscat',
  'beirut': 'Asia/Beirut',
  'tel aviv': 'Asia/Jerusalem',
  'amman': 'Asia/Amman',
  'cairo': 'Africa/Cairo',
  // Asia
  'delhi': 'Asia/Kolkata',
  'new delhi': 'Asia/Kolkata',
  'mumbai': 'Asia/Kolkata',
  'bangalore': 'Asia/Kolkata',
  'hyderabad': 'Asia/Kolkata',
  'chennai': 'Asia/Kolkata',
  'kolkata': 'Asia/Kolkata',
  'karachi': 'Asia/Karachi',
  'lahore': 'Asia/Karachi',
  'dhaka': 'Asia/Dhaka',
  'colombo': 'Asia/Colombo',
  'kathmandu': 'Asia/Kathmandu',
  'islamabad': 'Asia/Karachi',
  'beijing': 'Asia/Shanghai',
  'shanghai': 'Asia/Shanghai',
  'shenzhen': 'Asia/Shanghai',
  'guangzhou': 'Asia/Shanghai',
  'chengdu': 'Asia/Shanghai',
  'hong kong': 'Asia/Hong_Kong',
  'taipei': 'Asia/Taipei',
  'seoul': 'Asia/Seoul',
  'busan': 'Asia/Seoul',
  'tokyo': 'Asia/Tokyo',
  'osaka': 'Asia/Tokyo',
  'sapporo': 'Asia/Tokyo',
  'fukuoka': 'Asia/Tokyo',
  'singapore': 'Asia/Singapore',
  'kuala lumpur': 'Asia/Kuala_Lumpur',
  'kl': 'Asia/Kuala_Lumpur',
  'jakarta': 'Asia/Jakarta',
  'bali': 'Asia/Makassar',
  'denpasar': 'Asia/Makassar',
  'manila': 'Asia/Manila',
  'cebu': 'Asia/Manila',
  'bangkok': 'Asia/Bangkok',
  'phuket': 'Asia/Bangkok',
  'chiang mai': 'Asia/Bangkok',
  'ho chi minh city': 'Asia/Ho_Chi_Minh',
  'saigon': 'Asia/Ho_Chi_Minh',
  'hanoi': 'Asia/Bangkok',
  'phnom penh': 'Asia/Phnom_Penh',
  'vientiane': 'Asia/Vientiane',
  'yangon': 'Asia/Rangoon',
  'male': 'Indian/Maldives',
  'tashkent': 'Asia/Tashkent',
  'almaty': 'Asia/Almaty',
  // Australia & Pacific
  'sydney': 'Australia/Sydney',
  'melbourne': 'Australia/Melbourne',
  'melbourne airport': 'Australia/Melbourne',
  'brisbane': 'Australia/Brisbane',
  'perth': 'Australia/Perth',
  'adelaide': 'Australia/Adelaide',
  'darwin': 'Australia/Darwin',
  'canberra': 'Australia/Sydney',
  'hobart': 'Australia/Hobart',
  'auckland': 'Pacific/Auckland',
  'christchurch': 'Pacific/Auckland',
  'wellington': 'Pacific/Auckland',
  'fiji': 'Pacific/Fiji',
  'nadi': 'Pacific/Fiji',
  // Africa
  'johannesburg': 'Africa/Johannesburg',
  'cape town': 'Africa/Johannesburg',
  'durban': 'Africa/Johannesburg',
  'nairobi': 'Africa/Nairobi',
  'lagos': 'Africa/Lagos',
  'accra': 'Africa/Accra',
  'addis ababa': 'Africa/Addis_Ababa',
  'casablanca': 'Africa/Casablanca',
  'tunis': 'Africa/Tunis',
  'algiers': 'Africa/Algiers',
  'dar es salaam': 'Africa/Dar_es_Salaam',
  'kampala': 'Africa/Kampala',
  'kigali': 'Africa/Kigali',
  // Americas
  'new york': 'America/New_York',
  'boston': 'America/New_York',
  'washington': 'America/New_York',
  'miami': 'America/New_York',
  'atlanta': 'America/New_York',
  'toronto': 'America/Toronto',
  'montreal': 'America/Toronto',
  'ottawa': 'America/Toronto',
  'chicago': 'America/Chicago',
  'dallas': 'America/Chicago',
  'houston': 'America/Chicago',
  'minneapolis': 'America/Chicago',
  'denver': 'America/Denver',
  'salt lake city': 'America/Denver',
  'phoenix': 'America/Phoenix',
  'los angeles': 'America/Los_Angeles',
  'san francisco': 'America/Los_Angeles',
  'seattle': 'America/Los_Angeles',
  'las vegas': 'America/Los_Angeles',
  'portland': 'America/Los_Angeles',
  'vancouver': 'America/Vancouver',
  'calgary': 'America/Edmonton',
  'edmonton': 'America/Edmonton',
  'mexico city': 'America/Mexico_City',
  'cancun': 'America/Cancun',
  'sao paulo': 'America/Sao_Paulo',
  'rio de janeiro': 'America/Sao_Paulo',
  'buenos aires': 'America/Argentina/Buenos_Aires',
  'santiago': 'America/Santiago',
  'lima': 'America/Lima',
  'bogota': 'America/Bogota',
  'caracas': 'America/Caracas',
  'panama city': 'America/Panama',
  'havana': 'America/Havana',
};

/** Airports added 2026-09-26, after Melbourne → Queenstown showed 5h (it's ~3h):
 *  ZQN wasn't here, so one end fell back to raw clock subtraction. New Zealand,
 *  the Pacific, regional Australia and Asia, and European holiday airports. */
const MORE_AIRPORTS: Record<string, string> = {
  ZQN: 'Pacific/Auckland', DUD: 'Pacific/Auckland', NSN: 'Pacific/Auckland', NPL: 'Pacific/Auckland',
  NPE: 'Pacific/Auckland', ROT: 'Pacific/Auckland', TRG: 'Pacific/Auckland', IVC: 'Pacific/Auckland',
  HLZ: 'Pacific/Auckland', PMR: 'Pacific/Auckland', BHE: 'Pacific/Auckland', KKE: 'Pacific/Auckland',
  TUO: 'Pacific/Auckland', WSZ: 'Pacific/Auckland', GIS: 'Pacific/Auckland', TIU: 'Pacific/Auckland',
  OOL: 'Australia/Brisbane', TSV: 'Australia/Brisbane', MKY: 'Australia/Brisbane', ISA: 'Australia/Brisbane',
  HTI: 'Australia/Brisbane', PPP: 'Australia/Brisbane', MCY: 'Australia/Brisbane',
  LST: 'Australia/Hobart', ASP: 'Australia/Darwin', AYQ: 'Australia/Darwin', BME: 'Australia/Perth',
  KTA: 'Australia/Perth', NTL: 'Australia/Sydney', ARM: 'Australia/Sydney', ABX: 'Australia/Sydney',
  HIS: 'Australia/Hobart', LDH: 'Australia/Lord_Howe', NLK: 'Pacific/Norfolk',
  SUV: 'Pacific/Fiji', RAR: 'Pacific/Rarotonga', APW: 'Pacific/Apia', VLI: 'Pacific/Efate',
  TBU: 'Pacific/Tongatapu', KOA: 'Pacific/Honolulu', LIH: 'Pacific/Honolulu', SPN: 'Pacific/Saipan',
  LOP: 'Asia/Makassar', JOG: 'Asia/Jakarta', LGK: 'Asia/Kuala_Lumpur', KCH: 'Asia/Kuching',
  DMK: 'Asia/Bangkok', KBV: 'Asia/Bangkok', USM: 'Asia/Bangkok', CXR: 'Asia/Ho_Chi_Minh',
  PQC: 'Asia/Ho_Chi_Minh', REP: 'Asia/Phnom_Penh', LPQ: 'Asia/Vientiane', MLE: 'Indian/Maldives',
  PUS: 'Asia/Seoul', CJU: 'Asia/Seoul', ITM: 'Asia/Tokyo', FUK: 'Asia/Tokyo', OKA: 'Asia/Tokyo',
  KHH: 'Asia/Taipei', MFM: 'Asia/Macau', ZNZ: 'Africa/Dar_es_Salaam',
  HER: 'Europe/Athens', JTR: 'Europe/Athens', JMK: 'Europe/Athens', FNC: 'Atlantic/Madeira',
  PDL: 'Atlantic/Azores', LPA: 'Atlantic/Canary', TFS: 'Atlantic/Canary', IBZ: 'Europe/Madrid',
  KEF: 'Atlantic/Reykjavik', SPU: 'Europe/Zagreb', DBV: 'Europe/Zagreb', FLR: 'Europe/Rome',
  LIN: 'Europe/Rome', CTA: 'Europe/Rome', PMO: 'Europe/Rome', NCE: 'Europe/Paris', MRS: 'Europe/Paris',
  LYS: 'Europe/Paris', TLS: 'Europe/Paris', BOD: 'Europe/Paris', GYE: 'America/Guayaquil', HAV: 'America/Havana',
};
for (const [code, tz] of Object.entries(MORE_AIRPORTS)) AIRPORT_TZ[code] ??= tz;

/**
 * Countries with a single timezone (or a clear main one for travellers), by
 * the name Google Places and the parser write. Countries spanning several
 * zones are left out on purpose — Australia, the US, Canada, Brazil, Russia,
 * Mexico, Indonesia — and resolved by state/province or city instead, since
 * guessing one zone for them would be hours out.
 */
const COUNTRY_TZ: Record<string, string> = {
  'new zealand': 'Pacific/Auckland', 'aotearoa': 'Pacific/Auckland', 'fiji': 'Pacific/Fiji',
  'japan': 'Asia/Tokyo', 'south korea': 'Asia/Seoul', 'korea': 'Asia/Seoul', 'taiwan': 'Asia/Taipei',
  'china': 'Asia/Shanghai', 'hong kong': 'Asia/Hong_Kong', 'macau': 'Asia/Macau', 'singapore': 'Asia/Singapore',
  'malaysia': 'Asia/Kuala_Lumpur', 'thailand': 'Asia/Bangkok', 'vietnam': 'Asia/Ho_Chi_Minh', 'viet nam': 'Asia/Ho_Chi_Minh',
  'cambodia': 'Asia/Phnom_Penh', 'laos': 'Asia/Vientiane', 'myanmar': 'Asia/Yangon', 'philippines': 'Asia/Manila',
  'india': 'Asia/Kolkata', 'sri lanka': 'Asia/Colombo', 'nepal': 'Asia/Kathmandu', 'bhutan': 'Asia/Thimphu',
  'bangladesh': 'Asia/Dhaka', 'maldives': 'Indian/Maldives', 'pakistan': 'Asia/Karachi',
  'united arab emirates': 'Asia/Dubai', 'uae': 'Asia/Dubai', 'qatar': 'Asia/Qatar', 'oman': 'Asia/Muscat',
  'saudi arabia': 'Asia/Riyadh', 'jordan': 'Asia/Amman', 'israel': 'Asia/Jerusalem', 'lebanon': 'Asia/Beirut',
  'turkey': 'Europe/Istanbul', 'türkiye': 'Europe/Istanbul', 'egypt': 'Africa/Cairo', 'morocco': 'Africa/Casablanca',
  'south africa': 'Africa/Johannesburg', 'kenya': 'Africa/Nairobi', 'tanzania': 'Africa/Dar_es_Salaam',
  'ethiopia': 'Africa/Addis_Ababa', 'mauritius': 'Indian/Mauritius', 'seychelles': 'Indian/Mahe',
  'united kingdom': 'Europe/London', 'uk': 'Europe/London', 'england': 'Europe/London', 'scotland': 'Europe/London',
  'wales': 'Europe/London', 'ireland': 'Europe/Dublin', 'france': 'Europe/Paris', 'germany': 'Europe/Berlin',
  'italy': 'Europe/Rome', 'netherlands': 'Europe/Amsterdam', 'belgium': 'Europe/Brussels', 'switzerland': 'Europe/Zurich',
  'austria': 'Europe/Vienna', 'denmark': 'Europe/Copenhagen', 'sweden': 'Europe/Stockholm', 'norway': 'Europe/Oslo',
  'finland': 'Europe/Helsinki', 'iceland': 'Atlantic/Reykjavik', 'greece': 'Europe/Athens', 'croatia': 'Europe/Zagreb',
  'czechia': 'Europe/Prague', 'czech republic': 'Europe/Prague', 'hungary': 'Europe/Budapest', 'poland': 'Europe/Warsaw',
  'slovenia': 'Europe/Ljubljana', 'slovakia': 'Europe/Bratislava', 'romania': 'Europe/Bucharest', 'bulgaria': 'Europe/Sofia',
  'serbia': 'Europe/Belgrade', 'montenegro': 'Europe/Podgorica', 'albania': 'Europe/Tirane', 'malta': 'Europe/Malta',
  'cyprus': 'Asia/Nicosia', 'estonia': 'Europe/Tallinn', 'latvia': 'Europe/Riga', 'lithuania': 'Europe/Vilnius',
  'luxembourg': 'Europe/Luxembourg', 'monaco': 'Europe/Monaco',
  'argentina': 'America/Argentina/Buenos_Aires', 'colombia': 'America/Bogota', 'peru': 'America/Lima',
  'costa rica': 'America/Costa_Rica', 'panama': 'America/Panama', 'cuba': 'America/Havana', 'uruguay': 'America/Montevideo',
  'jamaica': 'America/Jamaica', 'bahamas': 'America/Nassau', 'puerto rico': 'America/Puerto_Rico',
};

/**
 * Multi-zone countries, by state or province. Abbreviations collide across
 * countries — WA is Western Australia or Washington, CA is California or
 * Canada — so each country has its own table, and with no country in the text
 * only the unambiguous entries (full names, and abbreviations nothing else
 * uses) are trusted.
 */
const REGION_TZ: Record<'australia' | 'canada' | 'usa', Record<string, string>> = {
  australia: {
    vic: 'Australia/Melbourne', victoria: 'Australia/Melbourne', nsw: 'Australia/Sydney',
    'new south wales': 'Australia/Sydney', act: 'Australia/Sydney', 'australian capital territory': 'Australia/Sydney',
    qld: 'Australia/Brisbane', queensland: 'Australia/Brisbane', tas: 'Australia/Hobart', tasmania: 'Australia/Hobart',
    sa: 'Australia/Adelaide', 'south australia': 'Australia/Adelaide', nt: 'Australia/Darwin',
    'northern territory': 'Australia/Darwin', wa: 'Australia/Perth', 'western australia': 'Australia/Perth',
  },
  canada: {
    bc: 'America/Vancouver', 'british columbia': 'America/Vancouver', ab: 'America/Edmonton', alberta: 'America/Edmonton',
    on: 'America/Toronto', ontario: 'America/Toronto', qc: 'America/Toronto', quebec: 'America/Toronto',
    ns: 'America/Halifax', 'nova scotia': 'America/Halifax',
  },
  usa: {
    ny: 'America/New_York', 'new york': 'America/New_York', ma: 'America/New_York', massachusetts: 'America/New_York',
    dc: 'America/New_York', fl: 'America/New_York', florida: 'America/New_York', nj: 'America/New_York',
    'new jersey': 'America/New_York', pa: 'America/New_York', pennsylvania: 'America/New_York',
    il: 'America/Chicago', illinois: 'America/Chicago', tx: 'America/Chicago', texas: 'America/Chicago',
    la: 'America/Chicago', louisiana: 'America/Chicago', tn: 'America/Chicago', tennessee: 'America/Chicago',
    co: 'America/Denver', colorado: 'America/Denver', ut: 'America/Denver', utah: 'America/Denver',
    az: 'America/Phoenix', arizona: 'America/Phoenix', nv: 'America/Los_Angeles', nevada: 'America/Los_Angeles',
    ca: 'America/Los_Angeles', california: 'America/Los_Angeles', wa: 'America/Los_Angeles', washington: 'America/Los_Angeles',
    or: 'America/Los_Angeles', oregon: 'America/Los_Angeles', hi: 'Pacific/Honolulu', hawaii: 'Pacific/Honolulu',
    ak: 'America/Anchorage', alaska: 'America/Anchorage',
  },
};
const COUNTRY_KEY: Record<string, keyof typeof REGION_TZ> = {
  australia: 'australia', canada: 'canada', usa: 'usa', us: 'usa', 'united states': 'usa', 'united states of america': 'usa',
};
/** Safe with no country named: full names, plus abbreviations only one table uses. */
const UNAMBIGUOUS_REGION: Record<string, string> = (() => {
  const seen = new Map<string, string[]>();
  for (const table of Object.values(REGION_TZ)) for (const [k, tz] of Object.entries(table)) seen.set(k, [...(seen.get(k) ?? []), tz]);
  const out: Record<string, string> = {};
  for (const [k, tzs] of seen) if (tzs.length === 1 && (k.length > 3 || !['sa', 'nt', 'on', 'or', 'co', 'la', 'ca', 'wa', 'ma', 'pa', 'il', 'hi', 'ab'].includes(k))) out[k] = tzs[0];
  delete out['washington']; delete out['victoria']; // also cities (Washington DC; Victoria, BC)
  return out;
})();

const isRealTz = (tz: string): boolean => {
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return true; } catch { return false; }
};

/** "Queenstown Airport" → "queenstown"; "Christchurch International Airport" → "christchurch". */
const placeKey = (text: string) => text.toLowerCase()
  .replace(/\([^)]*\)/g, ' ')
  .replace(/\b(international|intl|domestic|regional)?\s*(airport|terminal|station|railway station|ferry terminal)\b/g, ' ')
  .replace(/\b\d+\b/g, ' ') // postcodes: "Melbourne VIC 3000"
  .replace(/\s+/g, ' ').trim();

/**
 * Best-effort timezone resolution from any of the fields stored on a transport
 * record: explicit tz field → IATA code field → a code the place string
 * actually presents as one → city name.
 *
 * "Presents as one" is the whole care here. This used to take ANY three-letter
 * uppercase word in the place string as an airport code, and place strings are
 * full of words that are not: airlines and parsers write "Hobart TAS,
 * Australia" and "Vancouver CAN", where TAS is Tashkent and CAN is Guangzhou —
 * six and fifteen hours out. Shouty free text did it too; "HOTEL PICKUP FOR
 * GUESTS" resolved to Fortaleza.
 *
 * That is not a cosmetic error. calculatedDurationMinutes (tripUtils) resolves
 * both ends to compute a flight's length, and when the arrival end lands hours
 * behind the departure it returns null — so the duration silently disappeared
 * from the Itinerary and from the Day Planner's transit leg.
 *
 * Two forms are evidence, and nothing else is: a code in parentheses, which is
 * how every airline writes one, and a place string that is a bare code and
 * nothing else. A word sitting inside a sentence is a word.
 */
export function resolveTz(explicitTz?: string, code?: string, placeStr?: string, cityStr?: string): string {
  // A stored zone only counts if it's a real one — the parser has been known to
  // write offsets or city names there.
  if (explicitTz && isRealTz(explicitTz)) return explicitTz;
  if (code && AIRPORT_TZ[code.toUpperCase()]) return AIRPORT_TZ[code.toUpperCase()];
  if (placeStr) {
    const paren = placeStr.match(/\(([A-Za-z]{3})\)/)?.[1];
    if (paren && AIRPORT_TZ[paren.toUpperCase()]) return AIRPORT_TZ[paren.toUpperCase()];
    const only = placeStr.trim();
    if (/^[A-Za-z]{3}$/.test(only) && AIRPORT_TZ[only.toUpperCase()]) return AIRPORT_TZ[only.toUpperCase()];
  }
  // Then the words: every comma-separated part of the city and the place, most
  // specific first — a known city, then a state/province (only with a word in
  // front of it, so "CA" alone isn't read as California), then a country with
  // a single timezone. "Queenstown Airport, Frankton, New Zealand" resolves at
  // "new zealand"; "Hobart TAS, Australia" at "tas".
  const parts = [cityStr, placeStr].filter(Boolean)
    .flatMap(text => String(text).split(','))
    .map(placeKey).filter(Boolean);
  for (const part of parts) if (CITY_TZ[part]) return CITY_TZ[part];
  const country = parts.map(part => COUNTRY_KEY[part]).find(Boolean);
  const regions = country ? REGION_TZ[country] : UNAMBIGUOUS_REGION;
  for (const part of parts) {
    if (COUNTRY_KEY[part]) continue;
    if (regions[part]) return regions[part];
    const tail = part.split(' ').at(-1)!; // "Hobart TAS" → "tas"
    if (part.includes(' ') && regions[tail]) return regions[tail];
  }
  for (const part of [...parts].reverse()) if (COUNTRY_TZ[part]) return COUNTRY_TZ[part];
  return '';
}
